import {
  Activity,
  AlertTriangle,
  Archive,
  BarChart3,
  Calculator,
  Warehouse,
  CheckCircle,
  Clock,
  CreditCard,
  Headphones,
  Layers,
  LayoutDashboard,
  MessageSquare,
  Package,
  PackageCheck,
  PackageSearch,
  Palette,
  Phone,
  Printer,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Search,
  Shield,
  Shapes,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Tag,
  Ticket,
  Truck,
  Type,
  Users,
  Wallet,
  XCircle,
  Zap,
  Building2,
  CalendarDays,
  ClipboardList,
  FileText,
  HandCoins,
  PieChart,
  Settings,
  Timer,
} from "lucide-react";
import {
  canAccessPanel,
  canViewAnalytics,
  canViewOrderActivity,
  canViewOrders,
  canViewReplacementOrders,
  canViewReturns,
  canViewTickets,
  canViewFinancials,
  isAdmin,
  isOwner,
  isViewer,
} from "../utils/roles";
import { hasAnyPermission, hasPermission, isLegacyRole } from "../access/permissions";

/**
 * Data-driven admin navigation.
 * Add a future page by appending an item with a real route and an allow()
 * helper. Groups render only when at least one child is allowed.
 * Do not add inventory, warehouse, bazaar, or new roles until their routes
 * and permission helpers exist.
 */

/** Same gate as the previous dashboard payments button. */
export function canSeePaymentsNav(role) {
  return (isAdmin(role) || canViewAnalytics(role)) && canViewOrders(role);
}

/**
 * Customer analytics link follows the previous dashboard button (`isAdmin`:
 * Owner and Admin). The page itself still enforces `canViewCustomerAnalytics`.
 */
export function canSeeCustomerAnalyticsNav(role) {
  return isAdmin(role);
}

/** Viewers were excluded from design search on the previous dashboard. */
export function canSeeDesignSearchNav(role) {
  return canViewOrders(role) && !isViewer(role);
}

/** Home summary visibility. Financial amounts stay behind `canViewFinancials`. */
export function getOverviewAccess(role, access = null) {
  if (!isLegacyRole(role)) {
    const permissions = access?.permissions;
    const cards = hasPermission(role, permissions, "dashboard.cards.view");
    return {
      orders: cards,
      hero: hasPermission(role, permissions, "dashboard.hero.view"),
      amounts: hasPermission(role, permissions, "dashboard.cards.amounts"),
      tickets: false,
      replacements: false,
      returns: false,
      productionShortcut: hasPermission(role, permissions, "dashboard.production.open"),
      printletShortcut: hasPermission(role, permissions, "dashboard.printlet.open"),
      analyticsShortcut: false,
      designCategoriesShortcut: hasAnyPermission(role, permissions, ["admin.tools.design-categories.view"]),
      customerAnalyticsShortcut: false,
      paymentsShortcut: false,
    };
  }
  return {
    orders: canViewOrders(role),
    hero: isAdmin(role) && canViewOrders(role),
    amounts: canViewFinancials(role),
    tickets: canViewTickets(role),
    replacements: canViewReplacementOrders(role),
    returns: canViewReturns(role),
    productionShortcut: isAdmin(role),
    printletShortcut: isAdmin(role),
    analyticsShortcut: canViewAnalytics(role),
    designCategoriesShortcut: isAdmin(role),
    customerAnalyticsShortcut: isAdmin(role),
    paymentsShortcut: canSeePaymentsNav(role),
  };
}

export const ADMIN_NAV = [
  {
    id: "home",
    label: "الرئيسية",
    icon: LayoutDashboard,
    path: "/dashboard",
    allow: canAccessPanel,
    accessKeys: ["panel.access"],
  },
  {
    id: "orders",
    label: "الطلبات",
    icon: ShoppingCart,
    children: [
      { id: "new-orders", label: "الطلبات الجديدة", icon: ShoppingCart, path: "/new-orders", allow: canViewOrders, accessKeys: ["orders.view"], badgeKey: "newOrders" },
      { id: "confirmed-orders", label: "الطلبات المؤكدة", icon: CheckCircle, path: "/confirmed-orders", allow: canViewOrders, accessKeys: ["orders.view"], badgeKey: "confirmedOrders" },
      { id: "canceled-orders", label: "الطلبات الملغاة", icon: XCircle, path: "/canceled-orders", allow: canViewOrders, accessKeys: ["orders.view"], badgeKey: "canceledOrders" },
      { id: "hold-orders", label: "الطلبات المعلقة", icon: Clock, path: "/hold-orders", allow: canViewOrders, accessKeys: ["orders.view"], badgeKey: "holdOrders" },
      { id: "changes-orders", label: "تحت التغييرات", icon: RefreshCw, path: "/changes-orders", allow: canViewOrders, accessKeys: ["orders.view"], badgeKey: "changesOrders" },
      { id: "custom-orders", label: "التصاميم المخصصة", icon: Palette, path: "/custom-orders", allow: canViewOrders, accessKeys: ["orders.view"], badgeKey: "customOrders" },
      { id: "deposit-orders", label: "تتطلب دفعة أولية", icon: CreditCard, path: "/deposit-required-orders", allow: canViewOrders, accessKeys: ["orders.view"], badgeKey: "depositOrders" },
    ],
  },
  {
    id: "printing",
    label: "الطباعة والإنتاج",
    icon: Printer,
    children: [
      { id: "printed-orders", label: "Printed", icon: Printer, path: "/printed-orders", allow: canViewOrders, accessKeys: ["orders.view"] },
      { id: "girabatcom-orders", label: "Girabatcom", icon: ShoppingBag, path: "/girabatcom-orders", allow: canViewOrders, accessKeys: ["orders.view"] },
      { id: "printing-hold-orders", label: "Printing Hold", icon: Clock, path: "/printing-hold-orders", allow: canViewOrders, accessKeys: ["orders.view"] },
      { id: "incomplete-orders", label: "Incomplete", icon: AlertTriangle, path: "/incomplete-orders", allow: canViewOrders, accessKeys: ["orders.view"] },
      // Previous dashboard button was Owner/Admin. The page route still guards with canMutateOrders.
      { id: "production-dashboard", label: "لوحة الإنتاج", icon: Printer, path: "/production", allow: isAdmin, accessKeys: ["production.dashboard.view"] },
      { id: "printlet-orders", label: "طلبات Printlet", icon: PackageCheck, path: "/printlet-orders", allow: canViewOrders, accessKeys: ["orders.view"] },
      { id: "teeplus-orders", label: "Tee Plus", icon: Package, path: "/teeplus-orders", allow: canViewOrders, accessKeys: ["orders.view"] },
      { id: "printlet-dashboard", label: "لوحة Printlet", icon: PackageCheck, path: "/printlet", match: "prefix", allow: isAdmin, accessKeys: ["dashboard.printlet.open"] },
    ],
  },
  {
    id: "service",
    label: "خدمة العملاء",
    icon: Headphones,
    children: [
      { id: "tickets", label: "التذاكر", icon: Ticket, path: "/tickets", match: "prefix", allow: canViewTickets, badgeKey: "tickets" },
      { id: "replacement-orders", label: "طلبات الاستبدال", icon: RefreshCw, path: "/replacement-orders", allow: canViewReplacementOrders },
      { id: "returns", label: "المرتجعات", icon: RotateCcw, path: "/returns", allow: canViewReturns },
    ],
  },
  {
    id: "analytics",
    label: "التحليلات",
    icon: BarChart3,
    children: [
      { id: "main-analytics", label: "لوحة التحليلات", icon: BarChart3, path: "/analytics", allow: canViewAnalytics },
      { id: "customer-analytics", label: "تحليل العملاء", icon: Users, path: "/customer-analytics", allow: canSeeCustomerAnalyticsNav },
      { id: "fincart-analytics", label: "تحليلات Fincart", icon: Truck, path: "/fincart-analytics", allow: canViewAnalytics },
      { id: "production-analytics", label: "تحليل الإنتاج", icon: Activity, path: "/production/analytics", allow: isOwner },
      { id: "e7sebly", label: "احسبلي", icon: Calculator, path: "/e7sebly", allow: canViewFinancials },
      { id: "e7sebly-inventory", label: "قيمة المخزون", icon: Warehouse, path: "/e7sebly/inventory", allow: canViewFinancials },
      { id: "payments", label: "المدفوعات", icon: CreditCard, path: "/payments", match: "prefix", allow: canSeePaymentsNav },
    ],
  },
  {
    id: "catalog",
    label: "الكتالوج",
    icon: ShoppingBag,
    children: [
      { id: "category-types", label: "أنواع الفئات", icon: Shapes, path: "/admin/catalog/category-types", allow: isAdmin, accessKeys: ["catalog.manage"] },
      { id: "colors", label: "الألوان", icon: Palette, path: "/admin/catalog/colors", allow: isAdmin, accessKeys: ["catalog.manage"] },
      { id: "fonts", label: "خطوط التخصيص", icon: Type, path: "/admin/catalog/fonts", allow: isAdmin, accessKeys: ["catalog.manage"] },
      { id: "case-styles", label: "أنواع الكفر", icon: Smartphone, path: "/admin/catalog/case-styles", allow: isAdmin, accessKeys: ["catalog.manage"] },
      { id: "products", label: "المنتجات الجاهزة", icon: ShoppingBag, path: "/admin/catalog/products", match: "prefix", allow: isAdmin, accessKeys: ["catalog.manage"] },
    ],
  },
  {
    id: "store",
    label: "إدارة المتجر",
    icon: Layers,
    children: [
      { id: "design-categories", label: "فئات التصاميم", icon: Tag, path: "/categories", match: "prefix", allow: isAdmin, accessKeys: ["admin.tools.design-categories.view"] },
      { id: "brands", label: "أنواع الهواتف", icon: Smartphone, path: "/brands", match: "prefix", allow: isAdmin, accessKeys: ["admin.tools.phone-types.view"] },
      { id: "coupons", label: "أكواد الخصم", icon: Tag, path: "/coupons", allow: isAdmin },
      { id: "collections", label: "المجموعات", icon: Layers, path: "/collections", allow: isAdmin },
      { id: "bundles", label: "الباقات", icon: PackageSearch, path: "/bundles", match: "prefix", allow: isAdmin },
      { id: "merchandising", label: "تقرير المجموعات والباقات", icon: BarChart3, path: "/merchandising", allow: isAdmin },
      { id: "users", label: "إدارة الحسابات", icon: Users, path: "/users", allow: isAdmin },
      { id: "access-roles", label: "الأدوار والصلاحيات", icon: Shield, path: "/access", allow: isOwner },
    ],
  },
  {
    id: "operations",
    label: "التشغيل",
    icon: Zap,
    children: [
      { id: "follow-up-phones", label: "متابعة تأكيد WhatsApp", icon: Phone, path: "/follow-up-phones", allow: isAdmin },
      { id: "waiting-confirmation", label: "انتظار التأكيد", icon: Clock, path: "/waiting-confirming-orders", allow: isAdmin },
      { id: "execute-order", label: "نفذ الأوردر", icon: CheckCircle, path: "/confirming-orders", allow: isAdmin },
      { id: "cancelled-by-customer", label: "هيلغي", icon: XCircle, path: "/cancelled-by-customer-orders", allow: isAdmin },
      { id: "bulk-whatsapp", label: "إرسال قوالب WhatsApp", icon: MessageSquare, path: "/bulk-whatsapp", allow: isAdmin },
      { id: "bulk-status", label: "تحديث حالة جماعي", icon: RefreshCw, path: "/bulk-status-update", allow: isAdmin, accessKeys: ["orders.bulk-update-status"] },
    ],
  },
  {
    id: "search",
    label: "البحث",
    icon: Search,
    children: [
      { id: "search-orders", label: "البحث عن طلب", icon: Search, path: "/search-orders", allow: canViewOrders, accessKeys: ["orders.view"] },
      { id: "search-designs", label: "البحث عن تصميم", icon: PackageSearch, path: "/search-designs", allow: canSeeDesignSearchNav, accessKeys: ["designs.search"] },
    ],
  },
  {
    id: "hr",
    label: "الموارد البشرية",
    icon: Users,
    children: [
      { id: "hr-dashboard", label: "لوحة الموارد البشرية", icon: LayoutDashboard, path: "/hr", allow: (role, access) => canSeeAnyHr(role, access) },
      { id: "hr-employees", label: "الموظفون", icon: Users, path: "/hr/employees", match: "prefix", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ViewEmployees", "HR.ManageEmployees", "HR.ViewDepartmentEmployees"]) },
      { id: "hr-departments", label: "الأقسام والفرق", icon: Building2, path: "/hr/departments", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ManageDepartments", "HR.ViewDepartmentEmployees", "HR.ViewEmployees"]) },
      { id: "hr-attendance", label: "الحضور والإجازات", icon: CalendarDays, path: "/hr/attendance", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ViewAttendance", "HR.ManageAttendance", "HR.ApproveLeaves"]) },
      { id: "hr-performance", label: "الأداء والمؤشرات", icon: ClipboardList, path: "/hr/performance", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ViewPerformance", "HR.ManageKPIs"]) },
      { id: "hr-reports", label: "التقارير والملاحظات", icon: FileText, path: "/hr/reports", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ViewReports", "HR.CreateReports", "HR.ManageNotes"]) },
      { id: "hr-payroll", label: "الرواتب", icon: Wallet, path: "/hr/payroll", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ViewPayroll", "HR.ManagePayroll", "HR.ApprovePayroll"]) },
      { id: "hr-advances", label: "السلف", icon: HandCoins, path: "/hr/advances", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ViewAdvances", "HR.ManageAdvances", "HR.ApproveAdvances"]) },
      { id: "hr-overtime", label: "العمل الإضافي", icon: Timer, path: "/hr/overtime", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ViewOvertime", "HR.ManageOvertime", "HR.ApproveOvertime"]) },
      { id: "hr-analytics", label: "تحليلات الموارد البشرية", icon: PieChart, path: "/hr/analytics", allow: (role, access) => canSeeAnyHr(role, access) },
      { id: "hr-settings", label: "إعدادات الموارد البشرية", icon: Settings, path: "/hr/settings", allow: (role, access) => canSeeHrPermission(role, access, ["HR.ManageSettings", "HR.ManageDepartments"]) },
    ],
  },
  {
    id: "other",
    label: "أخرى",
    icon: Archive,
    children: [
      { id: "old-orders", label: "الطلبات القديمة", icon: Archive, path: "/old-orders", allow: canViewOrders, accessKeys: ["orders.archive.view"] },
      { id: "spam-orders", label: "الطلبات السبام", icon: AlertTriangle, path: "/spam-orders", allow: canViewOrders, accessKeys: ["orders.archive.view"] },
      { id: "order-activity", label: "سجل نشاط الطلبات", icon: ScrollText, path: "/admin/order-activity-log", allow: canViewOrderActivity, accessKeys: ["orders.activity-log.view"] },
    ],
  },
];

function normalizePath(pathname) {
  if (!pathname) return "/";
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

export function isNavPathActive(pathname, item) {
  if (!item?.path) return false;
  const current = normalizePath(pathname);
  const target = normalizePath(item.path);
  if (item.match === "prefix") {
    return current === target || current.startsWith(`${target}/`);
  }
  return current === target;
}

export function canSeeHrPermission(role, hrAccess, permissions) {
  if (isOwner(role)) return true;
  const grants = hrAccess?.permissions || [];
  return permissions.some((permission) => grants.some((grant) => grant.permission === permission));
}

export function canSeeAnyHr(role, hrAccess) {
  if (isOwner(role)) return true;
  return (hrAccess?.permissions || []).length > 0;
}

export function getVisibleNavigation(role, hrAccess = null, access = null) {
  const legacy = isLegacyRole(role);
  const visit = (items) => items.reduce((acc, item) => {
    if (item.children) {
      const children = visit(item.children);
      if (children.length === 0) return acc;
      acc.push({ ...item, children });
      return acc;
    }
    const allowed = legacy
      ? (typeof item.allow !== "function" || item.allow(role, hrAccess))
      : hasAnyPermission(role, access?.permissions, item.accessKeys);
    if (!allowed) return acc;
    acc.push(item);
    return acc;
  }, []);

  return visit(ADMIN_NAV);
}
